import { useState, useEffect, useRef, useCallback } from "react";

type Status = "idle" | "listening" | "processing" | "unsupported" | "denied";

interface UseSpeechInputOptions {
  onTranscript: (text: string) => void;
  lang?: string;
}

export function useSpeechInput({ onTranscript, lang = "en-US" }: UseSpeechInputOptions) {
  const [status, setStatus] = useState<Status>("idle");
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const stopCalledRef = useRef(false);

  const isSupported =
    typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  useEffect(() => {
    if (!isSupported) {
      setStatus("unsupported");
    }
  }, [isSupported]);

  const start = useCallback(() => {
    if (!isSupported) return;

    const SR =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    const recognition: SpeechRecognition = new SR();
    recognition.lang = lang;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    stopCalledRef.current = false;
    recognitionRef.current = recognition;

    recognition.onstart = () => setStatus("listening");

    recognition.onresult = (e: SpeechRecognitionEvent) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      if (final) {
        onTranscript(final.trim());
      } else if (interim) {
        onTranscript(interim.trim());
      }
    };

    recognition.onerror = (e: SpeechRecognitionErrorEvent) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setStatus("denied");
      } else {
        setStatus("idle");
      }
    };

    recognition.onend = () => {
      setStatus("idle");
      recognitionRef.current = null;
    };

    try {
      recognition.start();
    } catch {
      setStatus("idle");
    }
  }, [isSupported, lang, onTranscript]);

  const stop = useCallback(() => {
    stopCalledRef.current = true;
    recognitionRef.current?.stop();
    setStatus("idle");
  }, []);

  const toggle = useCallback(() => {
    if (status === "listening") stop();
    else start();
  }, [status, start, stop]);

  return { status, toggle, stop, isSupported };
}
